import React, { useState } from "react";
import { ArrowDown, ChevronDown, CircleArrowDown, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../shadcn/components/ui/Input";
import { Button } from "../shadcn/components/ui/Button";
import { Navbar } from "../Component/Navbar";
import community from "../assets/community/comunity.png";
import bgimg from "../assets/community/bg-img.png";
import { Footer } from "../Component/Footer";

export default function Community() {
  const [openAccordion, setOpenAccordion] = useState(null);
  const [openFAQ, setOpenFAQ] = useState([]);

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const toggleFAQ = (id) => {
    setOpenFAQ((prevOpenFAQ) =>
      prevOpenFAQ.includes(id)
        ? prevOpenFAQ.filter((item) => item !== id)
        : [...prevOpenFAQ, id]
    );
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        {/* Hero Section */}
        <div className="relative h-[300px] md:h-[500px] w-[90vw] md:w-[78vw] mb-8 rounded-3xl">
          <div className="absolute inset-0 bg-cover bg-center">
            <div className="absolute inset-0 bg-purple-500/20 z-10 rounded-3xl" />
            <img
              src={community}
              alt="community"
              className="h-[300px] md:h-[500px] w-[90vw] md:w-[84vw] z-10 rounded-3xl object-cover"
            />
          </div>
          <div className="relative h-full flex items-center justify-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white z-20">
              Community Resource
            </h1>
          </div>
        </div>

        <div className="container">
          {/* Search Section */}
          <div className="mb-8 mx-auto w-[78vw]">
            <div className="relative">
              <Input
                type="search"
                placeholder="        Search for Supported Topics, Resources and Questions"
                className="py-3 rounded-full placeholder:line-clamp-1"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Button className="absolute right-0 top-1/2 -translate-y-1/2 bg-purple-600 hover:bg-purple-700 rounded-full md:w-32">
                Search
              </Button>
            </div>
          </div>

          {/* Resource Categories */}
          <div className="space-y-4 mb-8 w-[78vw] mx-auto">
            {[
              "Support Groups",
              "Local Assistance Centers",
              "Health and Wellness Resources",
            ].map((item, index) => (
              <motion.div
                key={index}
                className="rounded-lg overflow-hidden"
                initial={false}
              >
                <motion.button
                  className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors duration-200 flex justify-between items-center"
                  onClick={() => toggleAccordion(`accordion-${index}`)}
                >
                  {item}
                  <motion.span
                    animate={{
                      rotate: openAccordion === `accordion-${index}` ? 180 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="text-purple-700" />
                  </motion.span>
                </motion.button>
                <AnimatePresence initial={false}>
                  {openAccordion === `accordion-${index}` && (
                    <motion.div
                      key={`content-${index}`}
                      initial="collapsed"
                      animate="open"
                      exit="collapsed"
                      variants={{
                        open: { opacity: 1, height: "auto" },
                        collapsed: { opacity: 0, height: 0 },
                      }}
                      transition={{
                        duration: 0.8,
                        ease: [0.04, 0.62, 0.23, 0.98],
                      }}
                    >
                      <div className="px-4 py-2 bg-white">
                        Content for {item} goes here...
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="mb-12 relative max-w-[84vw]">
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center w-[100vw] ml-auto opacity-20 pointer-events-none z-30"
              style={{ backgroundImage: `url(${bgimg})` }}
            />

            {/* FAQ Content */}
            <div className="relative w-[78vw] mx-auto">
              {/* Background Image */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-20 z-10"
                style={{ backgroundImage: `url(${bgimg})` }}
              />

              {/* FAQ Section */}
              <div className="relative z-20 ">
                <h2 className="text-2xl font-semibold mb-6 text-center">
                  Frequently Asked Questions
                </h2>
                {[
                  {
                    question:
                      "What is the best way to prevent common illnesses like colds and flu?",
                    answer:
                      "The best way to prevent common illnesses is by practicing good hygiene, such as washing hands frequently, avoiding close contact with sick individuals, staying hydrated, and getting a flu vaccine annually.",
                  },
                  {
                    question: "How often should I go for a routine check-up?",
                    answer:
                      "It is recommended to have a routine check-up at least once a year, or more frequently if advised by your healthcare provider.",
                  },
                  {
                    question:
                      "What should I do if I experience symptoms of COVID-19?",
                    answer:
                      "If you experience COVID-19 symptoms, isolate yourself, get tested, and contact your healthcare provider for guidance.",
                  },
                  {
                    question:
                      "How can I manage chronic conditions like diabetes or hypertension?",
                    answer:
                      "Managing chronic conditions requires regular monitoring, medication adherence, lifestyle modifications, and regular consultations with healthcare providers.",
                  },
                  {
                    question: "What are the benefits of getting vaccinated?",
                    answer:
                      "Vaccination helps prevent serious illnesses, protects vulnerable populations, and contributes to community immunity.",
                  },
                ].map((faq, index) => (
                  <motion.div
                    key={index}
                    className="border-b last:border-b-0 z-0 bg-[#F9F5F2] p-5 rounded-md shadow-md" // Use transparency for the dropdown background
                    initial={false}
                  >
                    <motion.button
                      className="w-full text-left py-4 flex justify-between items-center"
                      onClick={() => toggleFAQ(`faq-${index}`)}
                    >
                      <span className="font-bold text-sm md:text-lg">
                        {faq.question}
                      </span>
                      <motion.span
                        animate={{
                          rotate: openFAQ.includes(`faq-${index}`) ? 180 : 0,
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <CircleArrowDown className="text-purple-700" />
                      </motion.span>
                    </motion.button>
                    <AnimatePresence initial={false}>
                      {openFAQ.includes(`faq-${index}`) && (
                        <motion.div
                          key={`faq-content-${index}`}
                          initial="collapsed"
                          animate="open"
                          exit="collapsed"
                          variants={{
                            open: {
                              opacity: 1,
                              height: "auto",
                              marginBottom: "1rem",
                            },
                            collapsed: {
                              opacity: 0,
                              height: 0,
                              marginBottom: "0",
                            },
                          }}
                          transition={{
                            duration: 0.8,
                            ease: [0.04, 0.62, 0.23, 0.98],
                          }}
                        >
                          <div className="pb-4 text-sm md:text-lg">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Need Further Assistance Form */}
          <div className="mb-12 w-[78vw] mx-auto">
            <h2 className="text-2xl font-semibold mb-6 text-center">
              Need Further Assistance
            </h2>
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Name
                  </label>
                  <Input type="text" id="name" className="w-full" />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email
                  </label>
                  <Input type="email" id="email" className="w-full" />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Phone Number
                  </label>
                  <Input type="tel" id="phone" className="w-full" />
                </div>
                <div>
                  <label
                    htmlFor="location"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Location
                  </label>
                  <Input type="text" id="location" className="w-full" />
                </div>
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>
              <Button className="w-full bg-purple-600 hover:bg-purple-700">
                Submit
              </Button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
